/**
 * Slider with full-width media.
 */

const SFWM_SECTION = '.e-stn--slider-with-full-width-media';
const SFWM_NAV = '.e-tbs__nav';
const SFWM_CONTENT = '.e-tbs__content';
const SFWM_SLIDE_DIV = '.swiper-slide > .e-div';
const SFWM_PAGINATION = '.e-tbs__pagination';
const SFWM_DESKTOP_BP = 992;

const sfwmIsAdmin = () => document.body.classList.contains( 'wp-admin' );

/**
 * Move pagination outside the content swiper container.
 */
const sfwmMoveSectionPagination = ( section ) => {
  const content = section.querySelector( SFWM_CONTENT );
  if ( ! content ) return;

  const pagination = content.querySelector( SFWM_PAGINATION );
  if ( ! pagination ) return;

  content.after( pagination );
};

/**
 * Add logo from nav slide into one content slide.
 */
const sfwmSetupOneSlideLogo = ( navEl, slide ) => {
  const idx = slide.getAttribute( 'data-swiper-slide-index' );
  if ( idx === null ) return;

  const navSlide = navEl.querySelector(
    `.swiper-slide[data-swiper-slide-index="${ idx }"]`
  );
  if ( ! navSlide ) return;

  const imgEl = navSlide.querySelector( '.e-img' );
  if ( ! imgEl ) return;

  const logoEl = document.createElement( 'div' );
  logoEl.className = 'sfwm-active-logo';
  logoEl.innerHTML = imgEl.innerHTML;
  slide.appendChild( logoEl );
};

/**
 * Clone each nav logo into its matching content slide
 */
const sfwmSetupSectionLogos = ( section ) => {
  const navEl = section.querySelector( SFWM_NAV );
  const contentEl = section.querySelector( SFWM_CONTENT );
  if ( ! navEl || ! contentEl ) return;

  const contentDivs = contentEl.querySelectorAll( SFWM_SLIDE_DIV );
  contentDivs.forEach( ( div ) => {
    const slide = div.closest( '.swiper-slide' );
    if ( ! slide ) return;
    sfwmSetupOneSlideLogo( navEl, slide );
  } );
};

/**
 * Disable touch/swipe on the nav swiper on desktop
 */
const sfwmDisableNavSwipe = ( section ) => {
  if ( window.innerWidth < SFWM_DESKTOP_BP ) return;

  const navEl = section.querySelector( SFWM_NAV );
  if ( ! navEl?.swiper ) return;

  navEl.swiper.allowTouchMove = false;
};

const sfwmMakePeekEl = () => {
  const el = document.createElement( 'div' );
  el.className = 'sfwm-peek';
  el.style.cssText = 'position:absolute;top:0;pointer-events:none;';
  return el;
};

/**
 * Update left/right peek elements with current slide content and position.
 */
const sfwmApplyPeekStyles = ( swiper, wrapper, peekL, peekR ) => {
  if ( ! peekL.parentNode ) wrapper.appendChild( peekL );
  if ( ! peekR.parentNode ) wrapper.appendChild( peekR );

  if ( wrapper.querySelector( '[data-clone-of]' ) ) {
    peekL.style.display = 'none';
    peekR.style.display = 'none';
    return;
  }

  const slides = Array.from( wrapper.querySelectorAll( '.swiper-slide' ) );
  if ( slides.length < 2 ) return;

  const gap = parseInt( swiper.params.spaceBetween, 10 ) || 12;
  const w = slides[ 0 ].offsetWidth;
  const h = slides[ 0 ].offsetHeight;
  if ( ! w || ! h ) return;

  const size = 'width:' + w + 'px;height:' + h + 'px;';
  const baseStyle = 'position:absolute;top:0;pointer-events:none;' + size;

  peekL.innerHTML = slides[ slides.length - 1 ].innerHTML;
  peekL.style.cssText = baseStyle + 'left:' + ( -( w + gap ) ) + 'px;';

  peekR.innerHTML = slides[ 0 ].innerHTML;
  peekR.style.cssText = baseStyle + 'left:' + ( slides.length * ( w + gap ) ) + 'px;';
};

/**
 * Fix mobile infinite loop peek effect.
 *
 * On mobile the active slide is centred with adjacent slides peeking at the
 * edges.  When the active slide is the first or last in DOM order the
 * wrapping slide is physically on the other side of the wrapper, so there
 * is nothing to peek.  Two persistent absolutely-positioned "peek" elements
 * are created once and then shown / hidden / repositioned on each slide
 * change.  Because they are `position: absolute` they do not affect
 * Swiper's layout or slide count.
 */
const sfwmFixMobileLoop = ( section ) => {
  if ( window.innerWidth >= SFWM_DESKTOP_BP ) return;

  const contentEl = section.querySelector( SFWM_CONTENT );
  if ( ! contentEl?.swiper ) return;

  const swiper = contentEl.swiper;
  if ( ! swiper.params.loop ) return;

  const wrapper = swiper.wrapperEl;
  if ( ! wrapper ) return;

  const peekL = sfwmMakePeekEl();
  const peekR = sfwmMakePeekEl();
  wrapper.appendChild( peekL );
  wrapper.appendChild( peekR );

  const updatePeeks = () => sfwmApplyPeekStyles( swiper, wrapper, peekL, peekR );

  updatePeeks();
  swiper.on( 'slideChange', updatePeeks );
  swiper.on( 'slideChangeTransitionEnd', () => {
    requestAnimationFrame( updatePeeks );
  } );
};

/**
 * Ensure YouTube iframes have `enablejsapi=1`, `playsinline=1` and `mute=1`
 * so they autoplay reliably on every breakpoint. Non-YouTube iframes are
 * left as-is.
 */
const sfwmPrepareVideoIframes = ( section ) => {
  const iframes = section.querySelectorAll( '.e-vid--iframe iframe' );
  iframes.forEach( ( iframe ) => {
    const rawSrc = iframe.getAttribute( 'src' );
    if ( ! rawSrc ) return;

    let url;
    try {
      url = new URL( rawSrc, window.location.href );
    } catch ( e ) {
      console.error( 'sfwm iframe url parse error', e );
      return;
    }

    const isYouTube = /(^|\.)youtube(-nocookie)?\.com$/.test( url.hostname );
    if ( ! isYouTube ) return;

    iframe.dataset.sfwmYt = '1';

    const needsUpdate =
      url.searchParams.get( 'enablejsapi' ) !== '1' ||
      url.searchParams.get( 'playsinline' ) !== '1' ||
      url.searchParams.get( 'mute' ) !== '1';

    if ( ! needsUpdate ) return;

    url.searchParams.set( 'enablejsapi', '1' );
    url.searchParams.set( 'playsinline', '1' );
    url.searchParams.set( 'mute', '1' );
    iframe.setAttribute( 'src', url.toString() );
  } );
};

/**
 * Resolve the postMessage target origin for a YouTube embed iframe.
 */
const sfwmGetYoutubeIframeTargetOrigin = ( iframe ) => {
  const fallback = 'https://www.youtube.com';
  const rawSrc = iframe?.getAttribute?.( 'src' );
  if ( ! rawSrc ) return fallback;
  try {
    const url = new URL( rawSrc, window.location.href );
    if ( /(^|\.)youtube(-nocookie)?\.com$/.test( url.hostname ) ) {
      return url.origin;
    }
  } catch ( e ) {
    console.error( 'getYoutubeIframeTargetOrigin error', e );
    return fallback;
  }
  return fallback;
};

/**
 * Send a YouTube IFrame API command to an iframe via postMessage.
 */
const sfwmPostYT = ( iframe, func, args = [] ) => {
  if ( ! iframe?.contentWindow ) return;
  const targetOrigin = sfwmGetYoutubeIframeTargetOrigin( iframe );
  try {
    iframe.contentWindow.postMessage(
      JSON.stringify( { event: 'command', func, args } ),
      targetOrigin
    );
  } catch ( e ) {
    console.error( 'postYT error', e );
  }
};

/**
 * Start any native <video> and YouTube iframes inside a slide.  Playback
 * runs with sound where the browser allows it; if autoplay-with-sound is
 * blocked (common on first page load before any user gesture) we silently
 * fall back to muted playback so the slide isn't left frozen.
 */
const sfwmPlaySlideMedia = ( slide ) => {
  if ( ! slide ) return;

  slide.querySelectorAll( 'video' ).forEach( ( v ) => {
    try {
      v.removeAttribute( 'muted' );
      v.muted = false;
      const p = v.play();
      if ( p && typeof p.catch === 'function' ) {
        p.catch( () => {
          try {
            v.muted = true;
            v.play().catch( () => {} );
          } catch ( err ) {
            console.error( 'play video error', err );
          }
        } );
      }
    } catch ( e ) {
      console.error( 'play video error', e );
    }
  } );

  slide.querySelectorAll( 'iframe[data-sfwm-yt="1"]' ).forEach( ( f ) => {
    sfwmPostYT( f, 'playVideo' );
  } );
};

/**
 * Pause any native <video> and YouTube iframes inside a slide.  Playback
 * position is intentionally preserved so the next activation resumes from
 * where the user left off.
 */
const sfwmStopSlideMedia = ( slide ) => {
  if ( ! slide ) return;

  slide.querySelectorAll( 'video' ).forEach( ( v ) => {
    try {
      v.pause();
    } catch ( e ) {
      console.error( 'pause video error', e );
    }
  } );

  slide.querySelectorAll( 'iframe[data-sfwm-yt="1"]' ).forEach( ( f ) => {
    sfwmPostYT( f, 'pauseVideo' );
  } );
};

/**
 * Play media inside the currently active slide, stop all others.
 * Uses the `.swiper-slide-active` class so it works for both normal and
 * looped (cloned) slides.
 */
const sfwmUpdateActiveSlideMedia = ( section ) => {
  const contentEl = section.querySelector( SFWM_CONTENT );
  if ( ! contentEl ) return;

  contentEl.querySelectorAll( '.swiper-slide' ).forEach( ( slide ) => {
    if ( slide.classList.contains( 'swiper-slide-active' ) ) {
      sfwmPlaySlideMedia( slide );
    } else {
      sfwmStopSlideMedia( slide );
    }
  } );
};

/**
 * Size an iframe so it covers its parent (`.e-vid--iframe`) at a 16:9 ratio,
 * matching the `object-fit: cover` behavior of native <video> / <img>.
 *
 * Uses inline `!important` styles because the iframe usually carries a
 * utility class like `.w-100` which applies `width: 100% !important` — that
 * would otherwise shrink the iframe back to the parent's width and
 * letterbox the video on portrait slides (mobile / tablet).
 */
const sfwmSizeIframeToCover = ( iframe ) => {
  const parent = iframe.parentElement;
  if ( ! parent ) return;

  const rect = parent.getBoundingClientRect();
  const pw = rect.width || parent.clientWidth;
  const ph = rect.height || parent.clientHeight;
  if ( ! pw || ! ph ) return;

  const ratio = 16 / 9;
  const w = Math.max( pw, ph * ratio );
  const h = Math.max( ph, pw / ratio );

  const set = ( prop, value ) => {
    iframe.style.setProperty( prop, value, 'important' );
  };

  set( 'position', 'absolute' );
  set( 'top', '50%' );
  set( 'left', '50%' );
  set( 'width', w + 'px' );
  set( 'height', h + 'px' );
  set( 'max-width', 'none' );
  set( 'max-height', 'none' );
  set( 'transform', 'translate(-50%, -50%)' );
  set( 'border', '0' );
};

/**
 * Size every `.e-vid--iframe` iframe in a section to fully cover its slide.
 */
const sfwmSizeSectionIframes = ( section ) => {
  section.querySelectorAll( '.e-vid--iframe iframe' ).forEach(
    sfwmSizeIframeToCover
  );
};

/**
 * Observe `.e-vid--iframe` containers so iframes are resized whenever the
 * container's dimensions change (slide transitions, window resize, etc.).
 */
const sfwmObserveIframeContainers = ( section ) => {
  if ( typeof ResizeObserver === 'undefined' ) return;

  const containers = section.querySelectorAll( '.e-vid--iframe' );
  if ( ! containers.length ) return;

  const ro = new ResizeObserver( ( entries ) => {
    entries.forEach( ( { target } ) => {
      target.querySelectorAll( 'iframe' ).forEach( sfwmSizeIframeToCover );
    } );
  } );

  containers.forEach( ( el ) => ro.observe( el ) );
};

/**
 * Wire up media autoplay: play video in the active slide, pause it in every
 * other slide, and react to slide changes.  Handles native <video> elements
 * and YouTube iframes alike, on every breakpoint.
 */
const sfwmSetupMediaAutoplay = ( section ) => {
  const contentEl = section.querySelector( SFWM_CONTENT );
  if ( ! contentEl?.swiper ) return;

  sfwmPrepareVideoIframes( section );
  sfwmSizeSectionIframes( section );
  sfwmObserveIframeContainers( section );

  const update = () => {
    sfwmSizeSectionIframes( section );
    sfwmUpdateActiveSlideMedia( section );
  };

  // If a YouTube iframe finishes (re)loading after we set enablejsapi,
  // retry the play command so the active slide's video actually starts.
  section.querySelectorAll( 'iframe[data-sfwm-yt="1"]' ).forEach( ( iframe ) => {
    iframe.addEventListener( 'load', () => {
      sfwmSizeIframeToCover( iframe );
      const slide = iframe.closest( '.swiper-slide' );
      if ( slide?.classList.contains( 'swiper-slide-active' ) ) {
        setTimeout( () => {
          sfwmPostYT( iframe, 'playVideo' );
        }, 250 );
      }
    } );
  } );

  update();

  const swiper = contentEl.swiper;
  swiper.on( 'slideChange', update );
  swiper.on( 'slideChangeTransitionStart', update );
  swiper.on( 'slideChangeTransitionEnd', update );
  swiper.on( 'resize', () => sfwmSizeSectionIframes( section ) );
};

/**
 * Equalize content-div heights across all slides
 */
const sfwmEqualizeSlideHeights = ( section ) => {
  const contentEl = section.querySelector( SFWM_CONTENT );
  if ( ! contentEl ) return;

  const divs = contentEl.querySelectorAll( SFWM_SLIDE_DIV );
  if ( ! divs.length ) return;

  divs.forEach( ( el ) => { el.style.minHeight = ''; } );

  let maxH = 0;
  divs.forEach( ( el ) => {
    if ( el.offsetHeight > maxH ) maxH = el.offsetHeight;
  } );

  divs.forEach( ( el ) => { el.style.minHeight = `${ maxH }px`; } );
};

const sfwmInit = () => {
  if ( sfwmIsAdmin() ) return;
  document.querySelectorAll( SFWM_SECTION ).forEach(
    sfwmMoveSectionPagination
  );
};

const sfwmIsContentSwiperReady = ( sections ) => {
  const first = sections[ 0 ]?.querySelector( SFWM_CONTENT );
  return Boolean( first?.swiper );
};

const sfwmRunSectionInits = ( sections ) => {
  sections.forEach( ( section ) => {
    sfwmSetupSectionLogos( section );
    sfwmFixMobileLoop( section );
    sfwmDisableNavSwipe( section );
    sfwmEqualizeSlideHeights( section );
    sfwmSetupMediaAutoplay( section );
  } );
};

const sfwmInitSwiper = () => {
  if ( sfwmIsAdmin() ) return;

  let attempts = 0;
  const tryInit = () => {
    const sections = document.querySelectorAll( SFWM_SECTION );
    if ( ! sections.length ) return;

    if ( ! sfwmIsContentSwiperReady( sections ) && attempts < 20 ) {
      attempts++;
      setTimeout( tryInit, 20 );
      return;
    }

    sfwmRunSectionInits( sections );
  };

  setTimeout( tryInit, 20 );
};

const sfwmAddResizeListener = () => {
  let sfwmResizeTimer;
  window.addEventListener( 'resize', () => {
    clearTimeout( sfwmResizeTimer );
    sfwmResizeTimer = setTimeout( () => {
      document.querySelectorAll( SFWM_SECTION ).forEach( ( section ) => {
        sfwmEqualizeSlideHeights( section );
        sfwmSizeSectionIframes( section );
      } );
    }, 200 );
  } );
};

const sfwmBoot = () => {
  const state = document.readyState;
  const needsLoad = state === 'loading' || state === 'interactive';

  if ( state === 'loading' ) {
    document.addEventListener( 'DOMContentLoaded', sfwmInit );
  } else {
    sfwmInit();
  }

  if ( needsLoad ) {
    window.addEventListener( 'load', sfwmInitSwiper );
  } else {
    sfwmInitSwiper();
  }

  sfwmAddResizeListener();
};

sfwmBoot();